import React, { useEffect, useRef, useState } from "react";
import "./StatsCounter.css";

const stats = [
  { value: 10000, suffix: "+", label: "Trained Students" },
  { value: 20, suffix: "+", label: "Institutions" },
  { value: 12, suffix: "+", label: "Years of Experience" },
];

function useCountUp(target, duration = 2000, triggered) {
  const [count, setCount] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    // Reset to 0 whenever triggered flips to false (section left view)
    if (!triggered) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      setCount(0);
      return;
    }

    let startTime = null;

    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        setCount(target);
      }
    };

    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [triggered, target, duration]);

  return count;
}

function StatItem({ value, suffix, label, triggered, isLast }) {
  const count = useCountUp(value, 2000, triggered);

  const formatNumber = (num) => {
    if (num >= 1000) return num.toLocaleString("en-IN");
    return num.toString();
  };

  return (
    <div className={`sc__item ${!isLast ? "sc__item--divided" : ""}`}>
      <div className="sc__number">
        {formatNumber(count)}
        <span className="sc__suffix">{suffix}</span>
      </div>
      <div className="sc__label">{label}</div>
    </div>
  );
}

function StatsCounter() {
  const sectionRef = useRef(null);
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Fire true when entering, false when leaving — no one-time guard
        setTriggered(entry.isIntersecting);
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="sc__section" ref={sectionRef}>
      <div className="sc__container">
        {stats.map((stat, index) => (
          <StatItem
            key={stat.label}
            value={stat.value}
            suffix={stat.suffix}
            label={stat.label}
            triggered={triggered}
            isLast={index === stats.length - 1}
          />
        ))}
      </div>
    </section>
  );
}

export default StatsCounter;
